import {
  ControlType,
  CustomFieldDefinition,
  CustomFieldUsage,
  CustomFieldValueDefinition,
  FieldDataType,
  FieldPosition,
  FieldStatus,
} from '@alucio/aws-beacon-amplify/src/models';
import {
  SPECIAL_FIELDS,
  SUPPORTED_VEEVA_CONTROL_TYPES,
  VEEVA_VAULT_TABLES,
  VeevaVaultFormSetting,
  VeevaVaultLayoutSectionControl,
  VeevaVaultLookup,
  VeevaVaultObjectField,
  VeevaVaultObjectFieldType,
  VeevaVaultFieldRelationshipType,
  VeevaVaultFieldStatus,
  VeevaVaultObjectLayoutSection,
  VeevaVaultSectionLayoutItem,
  VeevaVaultSectionLayoutItemControl,
} from '../../CRMVeevaVaultTypes';
import logger from 'src/utils/logger';
import { getPartialCustomField } from './VeevaVaultTranslatorUtils';

type VeevaVaultSectionControlTypeHandler = {
  [controlReference: string]: (
    control: VeevaVaultLayoutSectionControl,
    veevaVaultFormSettings: VeevaVaultFormSetting[],
    processChild: (veevaVaultFormSetting: VeevaVaultFormSetting) => CustomFieldDefinition[],
    currentFormSetting?: VeevaVaultFormSetting,
  ) => CustomFieldDefinition[]
}

type VeevaVaultSpecialFieldHandler = {
  [tableName: string]: {
    [fieldName: string]: (
      item: VeevaVaultSectionLayoutItem,
      field: VeevaVaultObjectField) => CustomFieldDefinition[],
  } | undefined
}

const VEEVA_VAULT_SECTION_CONTROL_TYPE_HANDLER: VeevaVaultSectionControlTypeHandler = {
  [SUPPORTED_VEEVA_CONTROL_TYPES.MEDICAL_DISCUSSION_SECTION]: handleMedicalDiscussionSectionControl,
  [SUPPORTED_VEEVA_CONTROL_TYPES.CALL_DETAIL_SECTION]: handleChildSectionControl(VEEVA_VAULT_TABLES.CALL_DETAIL),
  [SUPPORTED_VEEVA_CONTROL_TYPES.CALL_DISCUSSION_SECTION]:
    handleChildSectionControl(VEEVA_VAULT_TABLES.CALL_DISCUSSION),
  [SUPPORTED_VEEVA_CONTROL_TYPES.ATTENDEE_SECTION]: handleAttendeesControlSection,
};

const VEEVA_VAULT_ITEM_CONTROL_TYPE_HANDLER = {
  [SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT]: handleBusinessAccount,
  [SUPPORTED_VEEVA_CONTROL_TYPES.DELIVERY_METHOD]: handleMedicalInquiryDeliveryMethod,
};

export enum MEDICAL_INQUIRY_SUPPORTED_METHODS {
  MAIL = 'mail__v',
  PHONE = 'phone_number__v',
  EMAIL = 'email__v',
  FAX = 'fax_number__v',
}

export const OMIT_FIELD_POSITION_SETTING: {[apiName: string]: string[]} = {
  [VEEVA_VAULT_TABLES.MEDICAL_INQUIRY]: [
    'delivery_method__v',
    ...Object.values(MEDICAL_INQUIRY_SUPPORTED_METHODS),
  ],
};

export const VEEVA_SPECIAL_ITEM_HANDLER: VeevaVaultSpecialFieldHandler = {
  [VEEVA_VAULT_TABLES.MEDICAL_INSIGHT]: {
    'account__v': handleCRMAccountSelect,
  },
  [VEEVA_VAULT_TABLES.MEDICAL_INQUIRY]: {
    'account__v': handleCRMAccountSelect,
  },
};

//* ITEM CONTROL TYPE HANDLERS * //
export function handleVeevaVaultItemControlType(
  item: VeevaVaultSectionLayoutItem,
): CustomFieldDefinition[] {
  const customFieldDefinition: CustomFieldDefinition[] = [];
  item.controls?.forEach((control) => {
    const controlItemHandler = VEEVA_VAULT_ITEM_CONTROL_TYPE_HANDLER[control.reference];
    if (controlItemHandler) {
      const controlCustomFields = controlItemHandler(control);
      customFieldDefinition.push(...controlCustomFields);
    } else {
      logger.veevaVaultTranslator.warn('No handler found for control item: ', control.reference);
    }
  });

  return customFieldDefinition;
}

//* SECTION CONTROL TYPE HANDLERS *//
export function handleVeevaVaultSectionControlType(
  section: VeevaVaultObjectLayoutSection,
  veevaVaultFormSettings: VeevaVaultFormSetting[],
  processChild: (veevaVaultFormSetting: VeevaVaultFormSetting) => CustomFieldDefinition[],
  currentFormSetting?: VeevaVaultFormSetting,
): CustomFieldDefinition[] {
  const customFieldDefinitions: CustomFieldDefinition[] = [];

  section.properties.controls?.forEach((control) => {
    const controlTypeHandler = VEEVA_VAULT_SECTION_CONTROL_TYPE_HANDLER[control.reference];
    if (controlTypeHandler) {
      const controlCustomFields =
        controlTypeHandler(control, veevaVaultFormSettings, processChild, currentFormSetting);
      customFieldDefinitions.push(...controlCustomFields);
    } else {
      logger.veevaVaultTranslator.warn('No handler found for control section: ', control.reference);
    }
  });

  return customFieldDefinitions;
}

function handleCRMAccountSelect(
  item: VeevaVaultSectionLayoutItem,
  field: VeevaVaultObjectField): CustomFieldDefinition[] {
  return [{
    ...getPartialCustomField(field),
    required: item.required || false,
    description: field.help_content || undefined,
    controlType: ControlType.CRMACCOUNTSEARCHER,
    fieldType: FieldDataType.CATEGORICAL,
  }];
}

function handleBusinessAccount(controlItem: VeevaVaultSectionLayoutItemControl): CustomFieldDefinition[] {
  let label = 'Location';

  controlItem.bindings.forEach((binding) => {
    if (binding.value.fieldLabel) {
      label = binding.value.fieldLabel;
    }
  });

  return [{
    id: SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT,
    fieldName: label,
    fieldLabel: '',
    status: FieldStatus.ENABLED,
    fieldType: FieldDataType.CATEGORICAL,
    required: false,
    dependentFieldId: 'primary_attendee_crm_account_id',
    displayOnParentSelection: true,
    controlType: ControlType.SELECT,
    usage: [CustomFieldUsage.MEETING],
    fieldValueDefinitions: [],
    createdAt: new Date().toISOString(),
    settings: { fieldPosition: FieldPosition.RIGHT },
  }];
}

function handleMedicalDiscussionSectionControl(
  _control: VeevaVaultLayoutSectionControl,
  veevaVaultFormSettings: VeevaVaultFormSetting[],
  processChild: (veevaVaultFormSetting: VeevaVaultFormSetting) => CustomFieldDefinition[],
): CustomFieldDefinition[] {
  const medicalDiscussionsFormSetting = veevaVaultFormSettings.filter((setting) =>
    setting.apiName === VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION);

  return medicalDiscussionsFormSetting.map((setting) => processChild(setting)).flat();
}

function handleChildSectionControl(apiName: string) {
  return (
    _control: VeevaVaultLayoutSectionControl,
    formSettings: VeevaVaultFormSetting[],
    processChild: (setting: VeevaVaultFormSetting) => CustomFieldDefinition[],
  ): CustomFieldDefinition[] => formSettings
    .filter(setting => setting.apiName === apiName &&
      setting.layout?.permissions.read && setting.layout.permissions.create)
    .filter(hasRequiredChildOptions)
    .flatMap(setting => processChild(setting));
}

function hasRequiredChildOptions(setting: VeevaVaultFormSetting): boolean {
  return setting.layout.sections.every(section => (section.properties.items || []).every(item => {
    const field = setting.objectInfo.fields.find(candidate => candidate.name === item.reference);
    const needsOptions = item.required && !item.read_only && item.status === VeevaVaultFieldStatus.ACTIVE &&
      field?.editable && !field.default_value &&
      field.name !== setting.parentTableRelationshipName;
    if (!needsOptions) {
      return true;
    }
    if (field.type === VeevaVaultObjectFieldType.PICKLIST) {
      return !!setting.picklists[field.name]?.picklistValues.length;
    }
    if (field.type === VeevaVaultObjectFieldType.OBJECT &&
      field.relationship_type === VeevaVaultFieldRelationshipType.REFERENCE) {
      return !!setting.lookups[field.name]?.length;
    }
    return true;
  }));
}

function handleAttendeesControlSection(
  control: VeevaVaultLayoutSectionControl,
  _veevaVaultFormSettings: VeevaVaultFormSetting[],
  _processChild: (veevaVaultFormSetting: VeevaVaultFormSetting) => CustomFieldDefinition[],
  currentFormSetting?: VeevaVaultFormSetting,
): CustomFieldDefinition[] {
  const customFieldDefinitions: CustomFieldDefinition[] = [];

  control.bindings?.forEach((binding) => {
    // "su" = SEARCH USER "MARK"
    if (
      binding.property === 'su' && binding.value === 'true' &&
      currentFormSetting?.lookups[SPECIAL_FIELDS.USERS_ATTENDEES]
    ) {
      const usersCustomField = getCallUsersMultiSelect(currentFormSetting?.lookups[SPECIAL_FIELDS.USERS_ATTENDEES]);
      customFieldDefinitions.push(usersCustomField);
    }
  })

  return customFieldDefinitions;
}

function getCallUsersMultiSelect(users: VeevaVaultLookup[]): CustomFieldDefinition {
  const fieldValueDefinitions: CustomFieldValueDefinition[] = [];

  users.forEach((user) => {
    fieldValueDefinitions.push({
      id: user.id,
      value: user.id,
      label: user.name__v,
      dependentValueIds: [],
      createdAt: new Date().toISOString(),
    });
  });

  return {
    id: SPECIAL_FIELDS.USERS_ATTENDEES,
    controlType: ControlType.SELECT,
    fieldType: FieldDataType.MULTICATEGORICAL,
    fieldLabel: 'Users',
    fieldName: SPECIAL_FIELDS.USERS_ATTENDEES,
    createdAt: new Date().toISOString(),
    usage: [CustomFieldUsage.MEETING],
    status: FieldStatus.ENABLED,
    fieldValueDefinitions,
  };
}

function handleMedicalInquiryDeliveryMethod(): CustomFieldDefinition[] {
  const dependencyFieldId = 'delivery_method__v';
  return [
    {
      id: MEDICAL_INQUIRY_SUPPORTED_METHODS.EMAIL,
      fieldName: MEDICAL_INQUIRY_SUPPORTED_METHODS.EMAIL,
      fieldLabel: 'Email',
      status: FieldStatus.ENABLED,
      fieldType: FieldDataType.CATEGORICAL,
      required: true,
      displayOnParentSelection: true,
      displayOnValueSelection: ['email__v', 'urgent_mail__v'],
      dependentFieldId: dependencyFieldId,
      controlType: ControlType.SELECT,
      usage: [CustomFieldUsage.MEETING],
      fieldValueDefinitions: [],
      createdAt: new Date().toISOString(),
    },
    {
      id: MEDICAL_INQUIRY_SUPPORTED_METHODS.FAX,
      fieldName: MEDICAL_INQUIRY_SUPPORTED_METHODS.FAX,
      fieldLabel: 'Fax',
      status: FieldStatus.ENABLED,
      fieldType: FieldDataType.CATEGORICAL,
      required: true,
      displayOnParentSelection: true,
      displayOnValueSelection: ['fax__v'],
      dependentFieldId: dependencyFieldId,
      controlType: ControlType.SELECT,
      usage: [CustomFieldUsage.MEETING],
      fieldValueDefinitions: [],
      createdAt: new Date().toISOString(),
    },
    {
      id: MEDICAL_INQUIRY_SUPPORTED_METHODS.MAIL,
      fieldName: MEDICAL_INQUIRY_SUPPORTED_METHODS.MAIL,
      fieldLabel: 'Mail',
      status: FieldStatus.ENABLED,
      fieldType: FieldDataType.CATEGORICAL,
      required: true,
      displayOnParentSelection: true,
      displayOnValueSelection: ['mail__v'],
      dependentFieldId: dependencyFieldId,
      controlType: ControlType.SELECT,
      usage: [CustomFieldUsage.MEETING],
      fieldValueDefinitions: [],
      createdAt: new Date().toISOString(),
    },
    {
      id: MEDICAL_INQUIRY_SUPPORTED_METHODS.PHONE,
      fieldName: MEDICAL_INQUIRY_SUPPORTED_METHODS.PHONE,
      fieldLabel: 'Phone',
      status: FieldStatus.ENABLED,
      fieldType: FieldDataType.CATEGORICAL,
      required: true,
      displayOnParentSelection: true,
      displayOnValueSelection: ['phone__v'],
      dependentFieldId: dependencyFieldId,
      controlType: ControlType.SELECT,
      usage: [CustomFieldUsage.MEETING],
      fieldValueDefinitions: [],
      createdAt: new Date().toISOString(),
    },
  ];
}
